import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Filesystem } from "@capacitor/filesystem";
import { Share } from "@capacitor/share";

/* -------------------- QUERY -------------------- */
function useQuery() {
  return new URLSearchParams(useLocation().search);
}

/* -------------------- LOAD PDF.JS -------------------- */
const loadPdfJs = (): Promise<any> =>
  new Promise((resolve, reject) => {
    const w = window as any;
    if (w.pdfjsLib) {
      w.pdfjsLib.GlobalWorkerOptions.workerSrc =
        "https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js";
      resolve(w.pdfjsLib);
      return;
    }

    const script = document.createElement("script");
    script.src = "https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.min.js";
    script.onload = () => {
      if (w.pdfjsLib) {
        w.pdfjsLib.GlobalWorkerOptions.workerSrc =
          "https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js";
        resolve(w.pdfjsLib);
      } else {
        reject(new Error("PDF.js load failed"));
      }
    };
    script.onerror = () => reject(new Error("PDF.js failed"));
    document.body.appendChild(script);
  });

/* -------------------- READ FILE SAFELY -------------------- */
const readPdfBytes = async (uri: string): Promise<Uint8Array> => {
  const file = await Filesystem.readFile({ path: uri });
  const binary = atob(file.data?.toString());
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
};

/* -------------------- COMPONENT -------------------- */
const DocumentViewer = () => {
  const q = useQuery();
  const navigate = useNavigate();

  const name = q.get("name") || "Document";
  const rawUri = q.get("uri") || "";

  const pdfRootRef = useRef<HTMLDivElement>(null);
  const abortRef = useRef(false);

  const [zoom, setZoom] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  /* -------------------- RENDER PDF -------------------- */
  useEffect(() => {
    abortRef.current = false;

    const render = async () => {
      setLoading(true);
      setError("");

      try {
        const pdfjs = await loadPdfJs();
        if (abortRef.current) return;

        const bytes = await readPdfBytes(rawUri);
        if (abortRef.current) return;

        const pdf = await pdfjs.getDocument({ data: bytes }).promise;
        if (abortRef.current) return;

        const root = pdfRootRef.current;
        if (!root) return;

        // 🔑 SAFE RESET (no removeChild)
        root.replaceChildren();

        for (let i = 1; i <= pdf.numPages; i++) {
          if (abortRef.current) return;

          const page = await pdf.getPage(i);
          const viewport = page.getViewport({ scale: zoom });

          const canvas = document.createElement("canvas");
          canvas.width = viewport.width;
          canvas.height = viewport.height;
          canvas.style.display = "block";
          canvas.style.margin = "0 auto 16px";

          root.appendChild(canvas);

          const ctx = canvas.getContext("2d");
          if (ctx) {
            await page.render({ canvasContext: ctx, viewport }).promise;
          }
        }
      } catch (e: any) {
        if (!abortRef.current) {
          setError(e?.message || "Failed to render PDF");
        }
      } finally {
        if (!abortRef.current) {
          setLoading(false);
        }
      }
    };

    render();

    return () => {
      abortRef.current = true;
    };
  }, [rawUri, zoom]);

  /* -------------------- OPEN -------------------- */
  const openExternally = async () => {
    try {
      await Share.share({
        title: name,
        files: [rawUri],
      });
    } catch {}
  };

  /* -------------------- UI -------------------- */
  return (
    <div
      style={{
        height: "100vh",
        background: "#151718",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* HEADER */}
      <div
        style={{
          padding: 12,
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <div style={{ color: "#fff" }}>{name}</div>
      </div>

      {/* BODY */}
      <div style={{ flex: 1, overflow: "auto", padding: 12 }}>
        {loading && <div style={{ color: "#aaa" }}>Loading…</div>}
        {error && <div style={{ color: "#ff6b6b" }}>{error}</div>}

        {/* 🔑 React never touches children */}
        <div ref={pdfRootRef} />

        <div
          style={{
            display: "flex",
            gap: 12,
            justifyContent: "center",
            alignItems: "center",
            marginTop: 8,
          }}
        >
          <div
            style={{
              width: 36,
              height: 36,
              border: "1px solid #555",
              borderRadius: 8,
              background: "#222",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <button
              style={{
                background: "transparent",
                border: "none",
                color: "#fff",
                width: "100%",
                height: "100%",
                fontSize: 16,
              }}
              onClick={() => setZoom((z) => Math.max(0.5, z - 0.1))}
            >
              −
            </button>
          </div>
          <div
            style={{
              width: 36,
              height: 36,
              border: "1px solid #555",
              borderRadius: 8,
              background: "#222",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <button
              style={{
                background: "transparent",
                border: "none",
                color: "#fff",
                width: "100%",
                height: "100%",
                fontSize: 16,
              }}
              onClick={() => setZoom((z) => Math.min(3, z + 0.1))}
            >
              +
            </button>
          </div>
          <button
            className="btn all_button_style text-black"
            onClick={openExternally}
          >
            Share
          </button>
        </div>
      </div>
    </div>
  );
};

export default DocumentViewer;
